import { RecordingsStore } from './recordings.store';
import { RecordingsQuery } from './recordings.query';
import { combineLatest, Observable, of } from 'rxjs';
import { debounceTime, delay, distinctUntilChanged, map, switchMap, take, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { SortType } from '../enum';
import { SyncApiService } from '../../shared/services/sync-api.service';
import { RecordingResponse } from './recording.response';
import { OrderOption, Recording, SortOption } from './recording.model';
import { RecordingRequest } from './recording.request';
import { Params, Router } from '@angular/router';
import { SearchTermsQuery } from '../../search/state';
import { RouterQuery } from '@datorama/akita-ng-router-store';
import { getPathFromUrl } from '../../shared/utils/router';
import { get } from 'lodash-es';
import { FilterSearchCriteria } from 'src/app/search/filters/state/filter.model';
import { ESearchScopes } from 'src/app/search/state/search-term.model';
import { SortOrder } from '../../asset-management/types';
import { TitleService } from 'src/app/shared/browser/title.service';
import { FeatureFlagQuery } from 'src/app/shared/feature-flag/state';
import { MusimapFiltersQuery } from '../../search/musimap-filters/musimap-services/musimap-filters.query';
import { asArray, cloneObject } from '../../abstract';
import { MUSIMAP_DIVISIONS } from 'src/app/search/musimap-filters/musimap-common/musimap-common.enum';
import { SearchTermType } from '../../shared/enums/typeahead';
import { SearchTermName } from '../../search/state/search-terms.enum';

const defaultParams = {
  limit: 50,
  offset: 0,
  sort: 'relevance',
  sortOrder: 'desc',
  scope: ESearchScopes.RECORDINGS,
  includeFilters: [],
  excludeFilters: [],
  rangeSearchFilter: [],
  divisions: [],
  clauses: [],
  savedFiltersOnly: true,
  chartDateFilters: [],
};

interface SearchParams {
  sort: SortType;
  sortOrder: SortOrder;
  scope: ESearchScopes;
}

@Injectable()
export class RecordingsService {
  public hasAudioFile?: boolean = true;
  public currentSearchTerm?: SearchTermType;

  constructor(
    private api: SyncApiService,
    private recordingsStore: RecordingsStore,
    private recordingsQuery: RecordingsQuery,
    private searchTermsQuery: SearchTermsQuery,
    private router: Router,
    private routerQuery: RouterQuery,
    private titleService: TitleService,
    private featureFlagQuery: FeatureFlagQuery,
    private musimapFiltersQuery: MusimapFiltersQuery
  ) {}

  lastRequest: RecordingRequest = defaultParams;
  savedFiltersOnly: boolean = true;

  selectSearchParams$: Observable<SearchParams> = combineLatest([
    this.recordingsQuery.selectSorting$,
    this.searchTermsQuery.selectSearchScope$,
    this.recordingsQuery.selectOrderOption$,
    this.searchTermsQuery.selectFilters$,
  ]).pipe(
    map(([sorting, scope, ordering, searchTerm]) => {
      const { name: sort } = sorting;
      const { name: sortOrder } = ordering;
      this.currentSearchTerm = searchTerm[0]?.type;
      return {
        sort,
        scope,
        sortOrder,
      };
    })
  );

  private hiddenParams: string[] = [
    'includeFilters',
    'excludeFilters',
    'clauses',
    'offset',
    'limit',
    'chartDateFilters',
  ];

  updateSearchScope(scope: ESearchScopes): void {
    this.recordingsStore.updateSearchScope(scope);
  }

  compareParams(a: RecordingRequest, b: RecordingRequest): boolean {
    return JSON.stringify(a) === JSON.stringify(b);
  }

  compareRequests(prev: FilterSearchCriteria, curr: FilterSearchCriteria, clearRequest: boolean = true): boolean {
    return !clearRequest || this.compareParams(prev, curr);
  }

  updateParams(params: RecordingRequest): void {
    const { /*scope = ESearchScopes.RECORDINGS,*/ sort = 'relevance' } = params;
    // this.updateSearchScope(scope);
    this.updateSortType(sort);
  }

  updateParamsOnce(params: Params): void {
    const state = this.recordingsQuery.getValue();
    const { requestsCount } = state;
    const firstRequest = requestsCount === 0;

    if (firstRequest) {
      this.updateParams(params);
    }
  }

  getRecordingsRequest(
    params: RecordingRequest = defaultParams,
    clearRequest: boolean = true,
    showCoverArts: boolean = true
  ): Observable<RecordingResponse> {
    const hasTermsSearchQuery = !!params.clauses?.length;
    const method = params.offset > 0 ? 'add' : 'set';

    if (
      (!hasTermsSearchQuery && !this.musimapFiltersQuery.needUpdate) ||
      (this.isAimsSearch && !params.clauses[0]?.queries?.[0])
    ) {
      this.recordingsStore.setLoading(false);
      return of();
    }

    this.lastRequest = params;
    this.recordingsStore.inecrementRequestCount();
    if (clearRequest) {
      this.recordingsStore.set([]);
    }
    this.recordingsStore.setLoading(true);
    const apiParams = this.getApiParams(cloneObject(params));
    this.processChartDateFilters(params, apiParams);

    apiParams.divisions = apiParams.divisions || this.musimapFiltersQuery.getDivisions();
    apiParams.thirdPartyOwned =
      apiParams.divisions.includes(MUSIMAP_DIVISIONS.THIRD_PARTY_OWNED) || !!params.thirdPartyOwned;
    // Exclude MUSIMAP_DIVISIONS MLR divisions from divisions list
    apiParams.divisions = apiParams.divisions.filter(
      element =>
        element !== MUSIMAP_DIVISIONS.THIRD_PARTY_OWNED &&
        element !== MUSIMAP_DIVISIONS.MLR &&
        element !== MUSIMAP_DIVISIONS.WMG_WCM_MLR &&
        element !== MUSIMAP_DIVISIONS.ALL
    );

    apiParams.rangeSearchFilter = apiParams.rangeSearchFilter.filter(filter => filter.name !== 'chart_dates');

    const justifyAimsFilters = () => {
      if (this.isAimsSearch) {
        params.hasAudioFile = true;
        apiParams.hasAudioFile = true;
        const clause = params.clauses[0];
        if (clause?.type === SearchTermType.Prompt) {
          apiParams.clauses = [];
          apiParams.text = clause.queries?.[0];
        } else if (clause?.type === SearchTermType.ExternalLink) {
          apiParams.clauses = [];
          apiParams.url = clause.queries?.[0];
        } else {
          this.currentSearchTerm === SearchTermType.Prompt ? (apiParams.text = '') : (apiParams.url = '');
        }
      } else {
        const clauses = [];
        const termSearch = cloneObject(params.clauses.find(c => c.type === SearchTermType.Term) || null);

        /** GLSS-10062:  quick fix, maybe we should update search logic in other level */
        const ignoreTags = asArray(this.searchTermsQuery.getActive())[0]?.name === SearchTermName.KEYWORD;

        if (termSearch?.tags && ignoreTags) {
          termSearch.queries = termSearch.tags.map(({ tag }) => tag);
          delete termSearch.tags;
        }

        params.clausesFilter?.forEach(c => {
          if (c.type === SearchTermType.ISRC) {
            if (termSearch) {
              Object.assign(termSearch, {
                queries: Array.from(new Set([].concat(termSearch.queries).concat(c.queries)).values()),
              });
            } else {
              clauses.push({
                type: SearchTermType.Term,
                queries: c.queries,
              });
            }
          } else {
            clauses.push({ ...c });
          }
        });

        const otherClauses = params.clauses.filter(c => c.type !== SearchTermType.Term);

        apiParams.clauses = [...(termSearch ? [termSearch, ...otherClauses] : [...otherClauses]), ...clauses];

        apiParams.clausesFilter = undefined;
      }
    };

    justifyAimsFilters();

    const urlSuffixes = {
      [SearchTermType.ExternalLink]: '/by-url',
      [SearchTermType.Prompt]: '/by-text',
    };

    const url = `songs/search${urlSuffixes[this.currentSearchTerm] ?? ''}`;

    return this.api.post(url, apiParams).pipe(
      switchMap((request: RecordingResponse) => {
        if (this.featureFlagQuery.getValueByKey('gls_4025_multitab')) {
          this.titleService.setBrowserTitle(this.getTabTitle(apiParams, request.page.total));
        }
        const { songs } = request;
        return params.scope === ESearchScopes.RECORDINGS && songs.length && showCoverArts
          ? this.getRecordingCoverArts(songs.map(s => s.id)).pipe(map(coverArts => ({ ...request, coverArts })))
          : of(request);
      }),
      tap((request: RecordingResponse & { coverArts: any }) => {
        const { songs, page, filters, activeFilters, coverArts } = request;
        const nextSongs = this.processSongs(songs, coverArts);
        this.recordingsStore[method](nextSongs);
        this.recordingsStore.updatePage(page);
        this.recordingsStore.updateFilters(filters);
        this.recordingsStore.updateActiveFilters(activeFilters);
        this.lastRequest = params;
      })
    );
  }

  private processChartDateFilters(params: RecordingRequest, apiParams: any): void {
    apiParams.chartDateFilters = [];

    const chartDatesItem = apiParams.rangeSearchFilter.find(filter => filter.name === 'chart_dates');
    if (chartDatesItem) {
      apiParams.chartDateFilters.push({ fromYear: chartDatesItem.min, toYear: chartDatesItem.max });
    }

    params.chartDateFilters.forEach(items => {
      if (items?.excludeFilter?.values.length) {
        items.excludeFilter.values.forEach(charts => {
          apiParams.chartDateFilters.push(charts);
        });
      }

      if (items?.includeFilter?.values.length) {
        items.includeFilter.values.forEach(charts => {
          apiParams.chartDateFilters.push(charts);
        });
      }
    });

    const currentYear = new Date().getFullYear();
    apiParams.chartDateFilters = apiParams.chartDateFilters.filter((item, index, self) => {
      const isDuplicate = item.fromYear === 1950 && item.toYear === currentYear;
      return (
        !isDuplicate || self.findIndex(filter => filter.fromYear === 1950 && filter.toYear === currentYear) === index
      );
    });
  }

  getTabTitle(apiParams: RecordingRequest, total: number): string {
    const query = [];
    const clauses = 'clauses';
    apiParams[clauses].forEach(element => {
      query.push(element.queries ? element.queries[0] : element.tags[0].tag);
    });
    return `${query.join(', ')}: ${total} // GLS`;
  }

  getRecordings(clearRequest: boolean = true, offset: number = 0): Observable<any> {
    return combineLatest([
      this.musimapFiltersQuery.params$,
      this.selectSearchParams$,
      this.routerQuery.select(state => (state.state ? state.state.url : '/')),
      this.searchTermsQuery.selectActiveTerms$,
    ]).pipe(
      debounceTime(1000),
      map(([filters, searchParams, url, terms]) => {
        if (getPathFromUrl(url as string) !== '/') {
          return;
        }

        const { sort, scope, sortOrder } = searchParams;

        const filtersParams = {
          ...defaultParams,
          ...filters,
        };

        return {
          ...filtersParams,
          ...terms,
          sort,
          scope,
          sortOrder,
          offset,
        };
      }),
      distinctUntilChanged((prev, curr) => this.compareRequests(prev, curr, clearRequest)),
      switchMap(params => {
        const routerState = this.routerQuery.getValue();
        if (getPathFromUrl(routerState.state.url) !== '/') {
          return of(null);
        }

        if (params.scope === ESearchScopes.WORKS) {
          params.sortOrder = SortOrder.asc;
        }

        return this.getRecordingsRequest(params, clearRequest);
      })
    );
  }

  redirectToSearch(nextParams: RecordingRequest = defaultParams): Promise<boolean> {
    const routerValue = this.routerQuery.getValue();
    const params = get(routerValue, 'state.root.queryParams', {});
    return this.router.navigate(['/'], {
      queryParams: this.filterUrlParams({ ...nextParams, ...params }),
    });
  }

  paginate(aimsIsEnabled: boolean = false): void {
    const { offset, hasNextPage } = this.recordingsQuery.getNextPage(aimsIsEnabled);
    this.recordingsStore.setLoading(true);
    if (hasNextPage) {
      this.getRecordings(false, offset)
        .pipe(take(1), delay(1000))
        .subscribe(() => {
          this.recordingsStore.update(state => ({
            ...state,
            page: {
              ...state.page,
              offset,
            },
            loading: false,
          }));
        });
    } else {
      this.recordingsStore.setLoading(false);
    }
  }

  updateSortType(nextSort: string | SortOption, force: boolean = false): void {
    const sort = force ? (nextSort as SortOption) : this.getSortOption(nextSort);
    this.recordingsStore.updateSortType(sort);
  }

  updateOrderType(nextOrder: string | OrderOption): void {
    const order = this.getOrderOption(nextOrder);
    this.recordingsStore.updateOrderType(order);
  }

  resetRequest(): void {
    this.lastRequest = defaultParams;
    this.updateOrderType(SortOrder.desc);
    this.updateSortType({ name: SortType.relevance, label: 'Relevancy' });
  }

  getHistoricalGenres(id: any): Observable<any> {
    return this.api.get<any>(`recording/${id}/historical-tags`).pipe(map(data => data.tags));
  }

  private getSortOption(sort: string | SortOption): SortOption {
    const state = this.recordingsQuery.getValue();
    const { sortOptions = [] } = state;
    const [defaultOption] = sortOptions;

    const searchTerm = typeof sort === 'string' ? sort : sort.name;
    const option = sortOptions.find(o => o.name === searchTerm);

    return option ? option : defaultOption;
  }

  private getOrderOption(order: string | OrderOption): OrderOption {
    const state = this.recordingsQuery.getValue();
    const { orderOptions = [] } = state;
    const [defaultOption] = orderOptions;

    const searchTerm = typeof order === 'string' ? order : order.name;
    const option = orderOptions.find(o => o.name === searchTerm);

    return option ? option : defaultOption;
  }

  private getApiParams(params: RecordingRequest): RecordingRequest {
    return {
      ...params,
      scope: `${params.scope}S` as ESearchScopes,
      includeCoverArtLink: false,
      includeExtendedSearchFilters: false,
      hasAudioFile: this.hasAudioFile,
      forMainSearch: !this.isAimsSearch,
      chartDateFilters: [],
    };
  }

  private processSongs(songs: Recording[], albumArts?: Record<number, string>): Recording[] {
    return songs.map((song: Recording) => {
      const { id, trackLink: originalTrackLink } = song;
      const trackLink = originalTrackLink && originalTrackLink !== 'N/A' ? originalTrackLink : null;
      const albumArtLink = albumArts?.[id];
      return {
        ...song,
        id,
        trackLink,
        albumArtLink,
      };
    });
  }

  public getRecordingCoverArts(ids: number[] | string): Observable<any> {
    return this.api.get(`recording/cover-art-paths?recordingIds=${asArray<string>(ids).join(',')}`);
  }

  private filterUrlParams(params: any): RecordingRequest {
    return Object.keys(params)
      .filter(k => !this.hiddenParams.includes(k) && params[k] !== defaultParams[k])
      .reduce((acc, k) => ({ ...acc, [k]: params[k] }), {});
  }

  private get isAimsSearch(): boolean {
    return [SearchTermType.ExternalLink, SearchTermType.Prompt].includes(this.currentSearchTerm);
  }

  setSavedFiltersOnly(value: boolean): void {
    this.savedFiltersOnly = value;
  }
}
